import React, { ReactNode } from "react";
import { Tab, Tabs, TabScrollButton, TabScrollButtonProps, TabsProps, TabProps, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import i18nPackage from "../../i18n/i18n";
import styles from "./Tabs.module.css";

export type TabsConfig = { key: string; label: ReactNode; icon?: string | React.ReactElement; id?: string };

type TabList = string[] | readonly string[] | TabsConfig[];

type CoreTabProps<T> = {
	value: T;
	tabs: TabList;
	className?: string;
	onChange?: (event: React.SyntheticEvent<Element, Event>, value: T) => void;
	width?: number | string;
	height?: number | string;
	tabsProps?: TabsProps;
	tabProps?: TabProps;
	secondary?: boolean;
	scrollable?: boolean;
	scrollButtons?: TabsProps["scrollButtons"];
};

export function CoreTabNumberIcon({ count = 0, isLoading }: { count?: number; isLoading?: boolean }) {
	if (isLoading) {
		return <Skeleton component="div" variant="circular" className={styles.iconCircle} />;
	}
	return (
		<div className={`${styles.iconCircle} ${styles.number}`}>
			{String(Math.min(count, 99)) + (count >= 100 ? "+" : "")}
		</div>
	);
}

const AccessibleScrollButton = (props: TabScrollButtonProps) => {
	const { direction, ...other } = props;

	return (
		<TabScrollButton
			{...other}
			direction={direction}
			role="button"
			tabIndex={0}
			aria-label={direction === "left" ? i18nPackage.t("coreComponents.tabs.previousButtonAriaLabel") : i18nPackage.t("coreComponents.tabs.nextButtonAriaLabel")}
		/>
	);
};

const StyledTabs = styled(Tabs)<{ secondary?: boolean }>(({ secondary }) => ({
	...(!secondary
		? {
				borderBottom: "1px solid #bbb7",
				"& .MuiTabs-indicator": {
					backgroundColor: "#6750A4",
				},
			}
		: {
				"&": {
					minHeight: 46,
				},
				"& .MuiTabs-scroller": {
					padding: "4px",
				},
				"& .MuiTabs-indicator": {
					display: "none",
				},
			}),
	"& .MuiTabScrollButton-root": {
		"&:focus-visible": {
			outline: "2px solid var(--focus-outline-color-dark)",
			outlineOffset: "-10px",
		},
	},
}));

const StyledTab = styled(Tab)<{ secondary?: boolean }>(({ secondary }) => ({
	...(secondary && {
		fontSize: "15px",
		color: "#2B2F32",
		padding: "0 16px",
		minHeight: 40,
		borderRadius: "6px",
		transition: "none",
		marginLeft: "3px",
		"&.Mui-selected": {
			backgroundColor: "#42296E",
			color: "#FFF",
			fontWeight: "700",
			border: "1px solid #DAD0EA",
			boxShadow: "box-shadow: 0 0 4px 0 #00000008",
			"& .MuiSkeleton-root": {
				backgroundColor: "rgba(255, 255, 255, 0.11)",
			},
			[`& .${styles.iconCircle}.${styles.number}`]: {
				backgroundColor: "white",
				border: "none",
				fontWeight: 700,
			},
		},
		"&.Mui-selected:hover, &.Mui-selected:focus-visible": {
			backgroundColor: "#311C56", // Hover for selected
		},
		"& .MuiTab-iconWrapper": {
			stroke: "#49454f",
			height: "16px",
		},
		"&.Mui-selected .MuiTab-iconWrapper": {
			stroke: "#FFF",
		},
		"&:focus-visible": {
			outline: "2px solid var(--focus-outline-color-dark)",
			outlineOffset: "1px",
		},
	}),
}));

export function CoreTabs<T>({
	tabs,
	width,
	height,
	scrollButtons = false,
	tabsProps,
	tabProps,
	...props
}: CoreTabProps<T>) {
	return (
		<StyledTabs
			{...props}
			{...tabsProps}
			variant={props.scrollable ? "scrollable" : "fullWidth"} // Use scrollable on small screens
			scrollButtons={scrollButtons}
			allowScrollButtonsMobile // Allow scroll buttons on mobile
			ScrollButtonComponent={AccessibleScrollButton}
			sx={{
				width: width,
				maxWidth: "100%",
				height: height,
				minHeight: height,
				...(props.secondary && {
					backgroundColor: "#F8FAFB",
					borderWidth: "1px",
					borderStyle: "solid",
					borderColor: "#21272D0A",
					borderRadius: "8px",
					overflowX: "hidden",
				}),

				...tabsProps?.sx,
			}}
			secondary={props.secondary}
		>
			{tabs.map((tab, index) => (
				<StyledTab
					{...tabProps}
					id={typeof tab === "string" ? undefined : tab.id}
					value={typeof tab === "string" ? index : (tab.key ?? index)}
					label={typeof tab === "string" ? tab : tab.label}
					key={typeof tab === "string" ? index : (tab.key ?? index)}
					iconPosition="start"
					icon={typeof tab === "string" ? undefined : (tab.icon ?? undefined)}
					disableRipple
					style={{
						height: height,
						minHeight: height,
						...tabProps?.style,
					}}
					secondary={props.secondary}
				/>
			))}
		</StyledTabs>
	);
}
